import type { Locale } from '@/i18n/config'
import { intlLocale } from '@/i18n/config'

export const formatMoney = (value: number | null | undefined, locale: Locale, currency = 'EUR', compact = false): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-'

    return new Intl.NumberFormat(intlLocale(locale), {
        style: 'currency',
        currency,
        maximumFractionDigits: compact ? 1 : 2,
        minimumFractionDigits: compact ? 0 : 2,
        notation: compact ? 'compact' : 'standard'
    }).format(value)
}

export const formatNumber = (value: number | null | undefined, locale: Locale, digits = 0): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-'
    return new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(value)
}

export const formatPercent = (value: number | null | undefined, locale: Locale, digits = 1): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-'
    return new Intl.NumberFormat(intlLocale(locale), { style: 'percent', maximumFractionDigits: digits, minimumFractionDigits: digits }).format(value)
}

export const formatDate = (value: string | null | undefined, locale: Locale, style: 'short' | 'medium' | 'long' = 'medium'): string => {
    if (!value) return '-'
    const date = new Date(value.length === 10 ? `${value}T12:00:00Z` : value)
    if (Number.isNaN(date.getTime())) return value

    const options: Intl.DateTimeFormatOptions = style === 'short'
        ? { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }
        : style === 'long'
            ? { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }
            : { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }

    return new Intl.DateTimeFormat(intlLocale(locale), options).format(date)
}

export const formatDateTime = (value: string | null | undefined, locale: Locale): string => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return new Intl.DateTimeFormat(intlLocale(locale), {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Belgrade'
    }).format(date)
}

export const formatPeriod = (period: string | null | undefined, locale: Locale): string => {
    if (!period) return '-'
    const [year, month] = period.split('-').map(Number)
    if (!year || !month) return period
    return new Intl.DateTimeFormat(intlLocale(locale), { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, 15)))
}

export const formatBytes = (bytes: number | null | undefined): string => {
    if (!bytes) return '0 KB'
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
